import { PLAN_UNKNOWN } from 'functions/entitlements/featureEntitlements';

/*
Load the signed-in user's plan, retrying transient backend failures and — when it
still cannot be determined — saying so explicitly rather than guessing.

WHY THIS EXISTS

`plan` is not a label, it is the join key the entitlement layer trusts: the value
is matched against a `tier` from GET /plans, and the matched plan's `features`
map decides what the UI unlocks. GET /plans really does publish the tier `free`,
which is the same string the login reducer defaults to when no plan is set.

So "we failed to load the plan" and "this user is on the free plan" were the same
state, and the caller could not tell them apart. A transient failure therefore
downgraded a paying Professional account to free entitlements — road-network
costs, multi-day, resupply, fixed assignments and heterogeneous travel costs all
locked, rows capped 250 -> 50, vehicles 50 -> 10, concurrent solves 10 -> 2 —
with nothing shown to the user, and (because loginData is persisted) surviving a
reload until some later sign-in happened to succeed.

That was not hypothetical. The backend load test on 2026-08-18 produced 76
`502 CLERK_API_ERROR` responses on exactly this endpoint, because /user_plan asks
Clerk about a subscription on every call. The backend has since put a cache in
front of it, which makes those failures rarer — not impossible. Any Clerk blip
does the same thing.

WHAT IT DOES

Retries a transient failure a few times, then reports PLAN_UNKNOWN. Callers must
treat that as "no information", never as a plan: the entitlement helpers fail open
on it, so an unentitled request is simply allowed through to the backend, which
enforces the real limit and returns a 403 the UI already renders as an upgrade
prompt. Letting a paying customer through to a clean 403 is a far better failure
than silently billing them for features the client has decided to hide.
*/

/** Attempts, including the first. */
export const PLAN_FETCH_ATTEMPTS = 3;

/** Backoff before attempt 2 and 3. Short: a signed-in user is waiting on this. */
export const PLAN_FETCH_BACKOFF_MS = [400, 1200];

/**
 * Whether a failed attempt is worth retrying.
 *
 * Only transient conditions are: 5xx and network/CORS errors (which arrive with
 * no status at all). A 4xx is a definite answer — retrying an auth or validation
 * failure just delays the outcome and multiplies load on an endpoint that is
 * already the one struggling.
 *
 * @param {Error & {status?: number}} error
 * @returns {boolean}
 */
export function isRetryablePlanError(error) {
  const status = error && typeof error.status === 'number' ? error.status : null;
  if (status === null) return true; // network failure — no response reached us
  return status >= 500;
}

/**
 * Whether a failed plan lookup is the backend's way of saying "free tier".
 *
 * /user_plan (and /can_user_save) ask Clerk's billing endpoint for the user's
 * subscription. Clerk answers 404 for a user who exists and simply has no paid
 * subscription, which is every free account, and the backend passes that through
 * as `404 USER_NOT_FOUND`. The code name is misleading, the meaning is not: the
 * backend's own entitlement layer resolves the same answer to the free tier.
 *
 * Only that exact pairing counts. Any other 4xx is still a definite failure to
 * learn the plan, and stays PLAN_UNKNOWN.
 *
 * @param {Error & {status?: number, code?: string}} error
 * @returns {boolean}
 */
export function isNoSubscriptionError(error) {
  return Boolean(error && error.status === 404 && error.code === 'USER_NOT_FOUND');
}

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Resolve the user's plan, or report that it could not be determined.
 *
 * @param {string} userId
 * @param {object} [deps]
 * @param {(userId: string) => Promise<object>} deps.fetchPlan - Injected for tests.
 * @param {(ms: number) => Promise<void>} [deps.sleep] - Injected for tests.
 * @returns {Promise<{plan: string, limits: object|null, resolved: boolean}>}
 *   `resolved: false` means the backend never answered; `plan` is PLAN_UNKNOWN
 *   and callers must not treat it as an entitlement claim.
 */
export async function loadUserPlan(userId, { fetchPlan, sleep = wait } = {}) {
  let lastError = null;

  for (let attempt = 0; attempt < PLAN_FETCH_ATTEMPTS; attempt += 1) {
    try {
      const response = await fetchPlan(userId);

      // A successful response carrying no plan is a real answer: the backend
      // knows this user and reports no subscription. That is the free tier, and
      // it is deliberately NOT treated as unknown.
      if (!response) {
        return { plan: 'free', limits: null, resolved: true };
      }

      return {
        plan: response.plan,
        limits: response.user_limits ?? null,
        resolved: true,
      };
    } catch (error) {
      // A free account with no subscription, reported as a 404. Same answer as
      // the empty-success branch above, and just as definite. Treating it as
      // unknown made the entitlement helpers fail open, so a free user was shown
      // paid features and then met a 403 for using them.
      if (isNoSubscriptionError(error)) {
        return { plan: 'free', limits: null, resolved: true };
      }
      lastError = error;
      if (!isRetryablePlanError(error)) break;

      const backoff = PLAN_FETCH_BACKOFF_MS[attempt];
      if (backoff === undefined) break; // that was the final attempt
      await sleep(backoff);
    }
  }

  return { plan: PLAN_UNKNOWN, limits: null, resolved: false, error: lastError };
}
